import React, { useEffect, useRef, useState } from 'react';
import { Button, Collapse, Empty, Input, Popconfirm, Spin, message } from 'antd';
import { ClearOutlined, RobotOutlined, SendOutlined, UserOutlined } from '@ant-design/icons';
import { knowledgeRagApi } from '../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';

const SourceList = ({ sources }) => (
  <div className="space-y-2">
    {sources.map((source) => (
      <div
        key={source.chunk_id}
        className="p-2 rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900"
      >
        <div className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">
          {source.source}. {source.knowledge_title}
          <span className="text-gray-400 font-normal mr-1">({source.category_name})</span>
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-300 m-0 leading-relaxed whitespace-pre-wrap">{source.content}</p>
      </div>
    ))}
  </div>
);

/**
 * پرسش از دستیار هوشمند: پاسخ فقط بر اساس دانش‌های ثبت‌شده. تاریخچه فقط در مرورگر نگهداری می‌شود.
 */
const KnowledgeTestChat = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  const send = async () => {
    const content = input.trim();
    if (!content || sending) return;

    const history = messages
      .filter((m) => !m.error)
      .map((m) => ({ role: m.role, content: m.content }));

    setMessages((prev) => [...prev, { role: 'user', content }]);
    setInput('');
    setSending(true);

    try {
      const res = await knowledgeRagApi.chat(content, history);
      const data = res?.data ?? {};
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: data.reply || '—', sources: data.retrieval?.sources || [] },
      ]);
    } catch (error) {
      const errorText = error?.message || 'پاسخی دریافت نشد. دوباره تلاش کنید.';
      message.error(errorText);
      setMessages((prev) => [...prev, { role: 'assistant', content: errorText, error: true }]);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="work-scroll-contain rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/40 flex flex-col h-[calc(100dvh-300px)] min-h-[360px]">
      {messages.length > 0 && (
        <div className="shrink-0 flex justify-end px-3 py-1 border-b border-gray-100 dark:border-gray-700">
          <Popconfirm title="گفتگو پاک شود؟" okText="بله" cancelText="انصراف" onConfirm={() => setMessages([])}>
            <Button size="small" type="text" icon={<ClearOutlined />} disabled={sending}>
              گفتگوی جدید
            </Button>
          </Popconfirm>
        </div>
      )}

      <div ref={listRef} className="work-scroll-pane flex-1 overscroll-contain px-3 sm:px-4 py-4 space-y-4">
        {messages.length === 0 && (
          <Empty
            className="mt-16"
            image={<RobotOutlined className="text-5xl text-purple-300" />}
            description={<span className="text-sm text-gray-500">سوال خود را درباره اطلاعات پایگاه دانش بپرسید.</span>}
          />
        )}

        {messages.map((m, idx) => (
          <div key={idx} className={`flex gap-2 ${m.role === 'user' ? 'flex-row' : 'flex-row-reverse'}`}>
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                m.role === 'user' ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/40' : 'bg-purple-100 text-purple-600 dark:bg-purple-900/40'
              }`}
            >
              {m.role === 'user' ? <UserOutlined /> : <RobotOutlined />}
            </div>
            <div className={`max-w-[85%] sm:max-w-[75%] min-w-0 ${m.role === 'user' ? '' : 'flex-1'}`}>
              <div
                className={`rounded-2xl px-3 py-2 text-sm leading-7 whitespace-pre-wrap break-words ${
                  m.role === 'user'
                    ? 'bg-indigo-600 text-white'
                    : m.error
                      ? 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-800'
                      : 'bg-white text-gray-800 border border-gray-100 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700'
                }`}
              >
                {m.content}
              </div>
              {m.sources?.length > 0 && (
                <Collapse
                  size="small"
                  ghost
                  className="mt-1"
                  items={[{
                    key: 'sources',
                    label: <span className="text-xs text-gray-500">منابع ({m.sources.length})</span>,
                    children: <SourceList sources={m.sources} />,
                  }]}
                />
              )}
            </div>
          </div>
        ))}

        {sending && (
          <div className="flex gap-2 flex-row-reverse items-center">
            <div className="w-8 h-8 rounded-full flex items-center justify-center bg-purple-100 text-purple-600 dark:bg-purple-900/40">
              <RobotOutlined />
            </div>
            <Spin size="small" />
          </div>
        )}
      </div>

      <div className="shrink-0 border-t border-gray-100 dark:border-gray-700 p-3 flex items-end gap-2">
        <Input.TextArea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="سوال خود را بنویسید..."
          autoSize={{ minRows: 1, maxRows: 5 }}
          maxLength={4000}
          disabled={sending}
        />
        <Button
          type="primary"
          icon={<SendOutlined className="rotate-180" />}
          onClick={send}
          loading={sending}
          disabled={!input.trim()}
          className={PANEL_PRIMARY_BTN_CLASS}
        >
          ارسال
        </Button>
      </div>
    </div>
  );
};

export default KnowledgeTestChat;
